import type { LocalImageProps } from '@/components/atoms/LocalImage';
import type { AccordionSectionConfigType } from '@/components/sections/AccordionSection';
import type { ContactSectionConfigType } from '@/components/sections/ContactSection';
import type { ImagesItemsSectionConfigType } from '@/components/sections/ImagesItemsSection';
import type { InlineTilesSectionProps } from '@/components/sections/InlineTilesSection/InlineTileSection.types';

export const PAYPO_LOGO_CONFIG: LocalImageProps = {
    src: '/images/logos/logo.svg',
    alt: 'Logo PayPo',
    width: 141,
    height: 40,
};

export const PAYU_LOGO_CONFIG: LocalImageProps = {
    src: '/images/logos/paypo-payu.svg',
    alt: 'Logo PayPo',
    width: 298,
    height: 52,
};

export const SECTION_REVIEWS_CONFIG: InlineTilesSectionProps = {
    headerContentText: {
        textConfig: [
            {
                type: 'heading2',
                text: 'Zobacz ponad 65 tys. pozytywnych opinii',
                className: 'heading-spaces text-center',
                'data-test-id': 'section-reviews-heading',
            },
        ],
    },
    containerConfig: {
        gridOptions: {
            columns: 3,
            gap: 'm',
        },
        swiperOptions: {
            swiperProps: {
                slidesPerView: 'auto',
                centerInsufficientSlides: true,
            },
            breakpoint: 'lg',
        },
        className: 'h-[360px] lg:h-full !overflow-visible',
        'data-test-id': 'section-reviews-container',
    },
    items: [
        {
            type: 'review-card',
            config: {
                rating: '5.0',
                name: 'Monika',
                content:
                    'Jestem bardzo zadowolona z płatności z PayPo. W każdej chwili, kiedy czegoś potrzebuję, mogę zrobić szybko i sprawnie zakupy, a potem w dogodnym terminie dokonać spłaty.',
            },
        },
        {
            type: 'review-card',
            config: {
                rating: '5.0',
                name: 'Magdalena',
                content:
                    'Rewelacja - jak dla mnie najszybszy i najwspanialszy sposób zapłaty za przesyłki, które mogę spokojnie obejrzeć, przetestować, przymierzyć czy sprawdzić w domu. Bez nerwów, pośpiechu i zbędnego zamrażania gotówki. A przy tym tak pewnie i bezpiecznie. POLECAM',
            },
        },
        {
            type: 'review-card',
            config: {
                rating: '5.0',
                name: 'Ala',
                content:
                    'Nigdy się nie zawiodłam na usłudze PayPo. Czytelne i jasne zasady. Dobry system powiadomień. Korzystam i będę korzystać.',
            },
        },
    ],
    additionalText: [
        {
            type: 'paragraph',
            className: 'text-3xs text-center text-grey-600',
            text: 'Prezentowane opinie pochodzą z serwisu Opineo. Weryfikacja opinii realizowana jest zgodnie z Regulaminem dla użytkowników serwisu Opineo.pl',
        },
    ],
    cta: {
        children: 'Zobacz opinie na Opineo',
        href: 'https://www.opineo.pl/opinie/paypo-pl',
        'data-test-id': 'section-reviews-opineo-link',
        variant: 'secondary',
    },
};

export const CONTACT_SECTION_CONFIG: ContactSectionConfigType = {
    title: [
        {
            type: 'heading4',
            text: 'Masz pytanie?',
            className: 'text-lg md:text-3xl text-center heading-spaces',
        },
    ],
    cardsContent: [
        {
            id: 1,
            className: 'bg-green-100 !p-8 justify-between',
            content: {
                titles: ['<a href="mailto:bok@paypo.pl">bok@paypo.pl</a>'],
                text: 'Na większość pytań odpowiadamy w ciągu 48 godzin.',
            },
            cta: {
                children: 'Napisz do nas',
                variant: 'primary',
                href: `mailto:bok@paypo.pl`,
                'data-test-id': 'contact-mail-redirect-button',
                className: 'text-nowrap',
            },
        },
        {
            id: 2,
            className: 'bg-purple-100 !p-8 justify-between',
            content: {
                titles: [
                    '<a className="text-black" href="tel:+48223337460">(+48) 22 333 74 60</a>',
                ],
                text: 'Infolinia czynna od poniedziałku do piątku, 8:00-18:00.',
            },
            cta: {
                children: 'Zadzwoń do nas',
                variant: 'secondary',
                href: `tel:48223337460`,
                'data-test-id': 'contact-tel-redirect-button',
                className: 'text-nowrap',
            },
        },
    ],
};

export const FAQ_QUESTIONS_CONFIG: AccordionSectionConfigType = {
    title: 'Najczęściej zadawane pytania',
    accordionContent: [
        {
            id: 1,
            title: 'Czy muszę podawać dane swojej karty kredytowej/debetowej?',
            content:
                'Nie, do skorzystania z płatności PayPo nie wymagamy podania danych karty kredytowej/debetowej.',
        },
        {
            id: 2,
            title: 'W jaki sposób płacę za zamówienie?',
            content:
                'Możesz zapłacić przelewem bankowym, przekazem pocztowym lub szybkimi płatnościami online - jak Ci wygodniej. Numer konta do spłaty otrzymasz mailem wraz z potwierdzeniem złożenia zamówienia. Dla Twojej wygody udostępniamy panel Klienta - nie masz obowiązku logować się do niego, ale możesz. Tam masz bieżący podgląd wszystkich ważnych informacji.',
        },
        {
            id: 3,
            title: 'Czy to jest bezpieczne?',
            content:
                'Tak, wszystkie płatności są realizowane w ramach szyfrowanego połączenia, a Ty nie podajesz żadnych danych dostępowych do swojego konta.',
        },
        {
            id: 4,
            title: 'Czy to coś kosztuje?',
            content:
                'Nie. Korzystając z usługi “Zapłać za 30 dni”, w ciągu 30 dni spłacasz tylko tyle, ile wynosi wartość zamówienia - bez dodatkowych kosztów.',
        },
        {
            id: 5,
            title: 'Do kiedy muszę spłacić transakcję?',
            content:
                'Masz aż 30 dni na spłatę swojego zamówienia. O zbliżającym się terminie spłaty przypomnimy Ci za pośrednictwem SMS i wiadomości e-mail.',
        },
        {
            id: 6,
            title: 'Co się stanie, jeśli zwrócę zamówienie?',
            content:
                'Po prostu nie zapłacisz. Twoja transakcja zostanie anulowana w naszym systemie.',
        },
    ],
};

export const BENEFITS_COMMON_SECTION_CONFIG: ImagesItemsSectionConfigType = {
    title: 'Korzyści',
    className: 'md:w-[40rem] my-0 mx-auto lg:w-full',
    list: [
        {
            id: '1',
            image: {
                src: '/images/icons/calendar.svg',
                alt: 'Ikona kalendarza',
            },
            description:
                '<b>Masz aż 30 dni na zapłatę</b> - bez dodatkowych kosztów',
        },
        {
            id: '2',
            image: {
                src: '/images/icons/boxes.svg',
                alt: 'Ikona paczek',
            },
            description:
                '<b>Wygodne doświadczenia zakupowe</b> - o terminach płatności przypominamy Ci mailowo i SMSem',
        },
        {
            id: '3',
            image: {
                src: '/images/icons/package.svg',
                alt: 'Ikona paczki',
            },
            description:
                '<b>Planujesz swoje wydatki</b> - płatność za zakupy rozciągasz w czasie i nie nadwyrężasz portfela, gdy akurat masz większe wydatki',
        },
        {
            id: '4',
            image: {
                src: '/images/icons/holding-gift.svg',
                alt: 'Ikona prezentu',
            },
            description:
                '<b>Możesz pozwolić sobie na więcej</b> - zaszaleć na wyprzedażach lub kupić prezent sobie czy bliskim, a płatność za zakupy rozciągnąć w czasie',
        },
        {
            id: '5',
            image: {
                src: '/images/icons/bag-security.svg',
                alt: 'Ikona bezpieczeństwo zakupów',
            },
            description:
                '<b>Robisz pewne zakupy</b> - sprawdzasz zamówienie, a płacisz później, gdy jesteś już pewny zakupu',
        },
        {
            id: '6',
            image: {
                src: '/images/icons/box-security.svg',
                alt: 'Ikona bezpieczeństwo zakupów',
            },
            description:
                '<b>Nie tracisz wyjątkowych okazji</b> - kupujesz, kiedy się najbardziej opłaca, a płacisz wygodnie później',
        },
        {
            id: '7',
            image: {
                src: '/images/icons/cash.svg',
                alt: 'Ikona pieniędzy',
            },
            description:
                '<b>Z czasem możesz jeszcze więcej</b> - Twój limit na kolejne zakupy może wzrosnąć aż do 6000 zł',
        },
        {
            id: '8',
            image: {
                src: '/images/icons/timer.svg',
                alt: 'Ikona timera',
            },
            description:
                '<b>Błyskawiczne i bezpieczne płatności</b> - kolejne transakcje potwierdzasz tylko kodem SMS lub jednym kliknięciem w aplikacji',
        },
    ],
};
